/**
 * Tutorial / Hint Overlay System
 * Shows contextual hints during early gameplay to teach basic mechanics.
 * Hints are shown as floating tooltip panels with fade-in/hold/fade-out animation.
 * Each hint is only shown once (tracked via localStorage).
 */

import { Renderer } from '@client/core/Renderer';
import { t } from '@locales/index';

/** All possible tutorial hint IDs */
export type TutorialHintId =
  | 'movement'
  | 'attack'
  | 'dodge'
  | 'pickup'
  | 'inventory'
  | 'skills'
  | 'potion'
  | 'equip'
  | 'socketing'
  | 'elements'
  | 'waypoint'
  | 'passive_tree';

/** Internal state for an active hint being displayed */
interface ActiveHint {
  id: TutorialHintId;
  text: string;
  timer: number;       // Total elapsed time for this hint
  duration: number;     // Total display duration (seconds)
  fadeInTime: number;   // Fade-in duration (seconds)
  fadeOutTime: number;  // Fade-out duration (seconds)
  dismissed: boolean;   // Manually dismissed (click/keypress)
}

const STORAGE_KEY = 'arpg_tutorial_seen';

/** Complete list of all hint IDs for the allComplete() check */
const ALL_HINT_IDS: TutorialHintId[] = [
  'movement', 'attack', 'dodge', 'pickup', 'inventory', 'skills', 'potion',
  'equip', 'socketing', 'elements', 'waypoint', 'passive_tree',
];

// Total display time for each hint
const HINT_DURATION = 5.0;
const FADE_IN_TIME = 0.4;
const FADE_OUT_TIME = 0.6;

export class TutorialSystem {
  /** Set of hint IDs that have already been shown (persisted in localStorage) */
  private seenHints: Set<TutorialHintId>;

  /** Currently displayed hint (only one at a time) */
  private activeHint: ActiveHint | null = null;

  /** Queue of hints waiting to be shown (in case multiple trigger close together) */
  private queue: TutorialHintId[] = [];

  /** Whether the tutorial system is enabled */
  private enabled: boolean = true;

  constructor() {
    this.seenHints = this.loadSeenHints();
  }

  // ── Persistence ──────────────────────────────────────────────────────

  private loadSeenHints(): Set<TutorialHintId> {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const arr = JSON.parse(raw) as string[];
        return new Set(arr as TutorialHintId[]);
      }
    } catch {
      // Corrupt data – start fresh
    }
    return new Set();
  }

  private saveSeenHints(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify([...this.seenHints]));
    } catch {
      // localStorage full or blocked – ignore
    }
  }

  // ── Public API ───────────────────────────────────────────────────────

  /**
   * Trigger a hint to be shown. If the hint was already shown (or is currently
   * showing), this is a no-op.
   */
  triggerHint(hintId: TutorialHintId): void {
    if (!this.enabled) return;
    if (this.seenHints.has(hintId)) return;

    // Already showing or queued
    if (this.activeHint?.id === hintId) return;
    if (this.queue.includes(hintId)) return;

    // Mark as seen immediately so it won't trigger again
    this.seenHints.add(hintId);
    this.saveSeenHints();

    if (this.activeHint) {
      // Another hint is active – queue this one
      this.queue.push(hintId);
    } else {
      this.showHint(hintId);
    }
  }

  /**
   * Dismiss the current hint immediately (e.g. on click or keypress).
   */
  dismiss(): void {
    if (this.activeHint && !this.activeHint.dismissed) {
      this.activeHint.dismissed = true;
    }
  }

  /**
   * Reset all tutorial progress (e.g. for a fresh start).
   */
  reset(): void {
    this.seenHints.clear();
    this.activeHint = null;
    this.queue = [];
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch { /* ignore */ }
  }

  /**
   * Check if a specific hint has already been shown.
   */
  hasBeenShown(hintId: TutorialHintId): boolean {
    return this.seenHints.has(hintId);
  }

  /**
   * Check if all tutorial hints have been shown already.
   */
  allComplete(): boolean {
    return this.seenHints.size >= ALL_HINT_IDS.length;
  }

  // ── Internal ─────────────────────────────────────────────────────────

  private showHint(hintId: TutorialHintId): void {
    const text = t(`tutorial.${hintId}`);
    this.activeHint = {
      id: hintId,
      text,
      timer: 0,
      duration: HINT_DURATION,
      fadeInTime: FADE_IN_TIME,
      fadeOutTime: FADE_OUT_TIME,
      dismissed: false,
    };
  }

  private advanceQueue(): void {
    this.activeHint = null;
    if (this.queue.length > 0) {
      const next = this.queue.shift()!;
      this.showHint(next);
    }
  }

  // ── Update & Render ──────────────────────────────────────────────────

  update(dt: number): void {
    if (!this.activeHint) return;

    this.activeHint.timer += dt;

    // If dismissed, fast-forward into fade-out
    if (this.activeHint.dismissed) {
      const holdEnd = this.activeHint.duration - this.activeHint.fadeOutTime;
      if (this.activeHint.timer < holdEnd) {
        this.activeHint.timer = holdEnd;
      }
    }

    // Hint finished
    if (this.activeHint.timer >= this.activeHint.duration) {
      this.advanceQueue();
    }
  }

  render(renderer: Renderer): void {
    if (!this.activeHint) return;

    const hint = this.activeHint;
    const ctx = renderer.context;
    const w = renderer.width;

    // Calculate alpha based on animation phase
    const alpha = this.calculateAlpha(hint);
    if (alpha <= 0) return;

    ctx.save();
    ctx.globalAlpha = alpha;

    // ── Measure text ────────────────────────────────────────────────
    const fontSize = 15;
    const font = `bold ${fontSize}px Arial, sans-serif`;
    ctx.font = font;
    const textMetrics = ctx.measureText(hint.text);
    const textWidth = textMetrics.width;

    // Panel dimensions
    const padH = 18; // horizontal padding
    const padV = 12; // vertical padding
    const panelW = textWidth + padH * 2;
    const panelH = fontSize + padV * 2;

    // Position: top-center of screen, below the zone name area
    const panelX = (w - panelW) / 2;
    const panelY = 72;

    // Slight slide-up animation during fade-in
    const slideOffset = hint.timer < hint.fadeInTime
      ? (1 - hint.timer / hint.fadeInTime) * 8
      : 0;
    const finalY = panelY + slideOffset;

    // ── Draw panel background ───────────────────────────────────────
    // Rounded rectangle with semi-transparent dark background
    const radius = 8;
    ctx.beginPath();
    ctx.roundRect(panelX, finalY, panelW, panelH, radius);

    // Dark fill
    ctx.fillStyle = 'rgba(10, 8, 16, 0.85)';
    ctx.fill();

    // Gold border
    ctx.strokeStyle = 'rgba(255, 215, 0, 0.6)';
    ctx.lineWidth = 1.5;
    ctx.stroke();

    // Subtle inner glow along the top edge
    const glowGrad = ctx.createLinearGradient(panelX, finalY, panelX, finalY + 6);
    glowGrad.addColorStop(0, 'rgba(255, 215, 0, 0.12)');
    glowGrad.addColorStop(1, 'rgba(255, 215, 0, 0)');
    ctx.fillStyle = glowGrad;
    ctx.beginPath();
    ctx.roundRect(panelX + 1, finalY + 1, panelW - 2, 6, [radius - 1, radius - 1, 0, 0]);
    ctx.fill();

    // ── Draw hint icon (small diamond) ──────────────────────────────
    const iconX = panelX + padH - 2;
    const iconY = finalY + panelH / 2;
    ctx.fillStyle = '#ffd700';
    ctx.beginPath();
    ctx.moveTo(iconX, iconY - 4);
    ctx.lineTo(iconX + 4, iconY);
    ctx.lineTo(iconX, iconY + 4);
    ctx.lineTo(iconX - 4, iconY);
    ctx.closePath();
    ctx.fill();

    // ── Draw text ───────────────────────────────────────────────────
    ctx.font = font;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';

    // Text shadow for readability
    ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
    ctx.fillText(hint.text, panelX + padH + 10 + 1, finalY + panelH / 2 + 1);

    // Main text in warm gold/cream
    ctx.fillStyle = '#f4e8d4';
    ctx.fillText(hint.text, panelX + padH + 10, finalY + panelH / 2);

    // ── Draw dismiss hint ───────────────────────────────────────────
    const dismissText = t('tutorial.dismissHint');
    ctx.font = `11px Arial, sans-serif`;
    ctx.textAlign = 'right';
    ctx.fillStyle = 'rgba(180, 170, 150, 0.6)';
    ctx.fillText(dismissText, panelX + panelW - padH + 4, finalY + panelH / 2);

    ctx.restore();
  }

  private calculateAlpha(hint: ActiveHint): number {
    const { timer, duration, fadeInTime, fadeOutTime } = hint;

    if (timer < fadeInTime) {
      // Fade in
      return timer / fadeInTime;
    }

    const fadeOutStart = duration - fadeOutTime;
    if (timer > fadeOutStart) {
      // Fade out
      return Math.max(0, 1 - (timer - fadeOutStart) / fadeOutTime);
    }

    // Fully visible (hold phase)
    return 1;
  }
}
